import type { Board, Outfit } from '@closetly/core';
import { newRecordId } from './ids';
import { useBoards, useOutfits, useSaveBoard, useSaveOutfit } from './queries';
import { useToast } from './toast';

/** What can be saved where: a piece to boards and outfits, an outfit to boards. */
export type SaveTarget = { type: 'item'; id: string; name: string } | { type: 'outfit'; id: string; name: string };

/** Saving and unsaving to boards and outfits, applied right away (Pinterest's "Merken"). */
export function useMembership(uid: string) {
  const boards = useBoards(uid);
  const outfits = useOutfits(uid);
  const saveBoard = useSaveBoard(uid);
  const saveOutfit = useSaveOutfit(uid);
  const toast = useToast();
  const failed = () => toast('Speichern fehlgeschlagen. Versuch es nochmal.');

  const field = (t: SaveTarget) => (t.type === 'item' ? 'items' : 'outfits');

  return {
    boards: boards.data ?? [],
    outfits: outfits.data ?? [],

    inBoard: (b: Board, t: SaveTarget) => b[field(t)].includes(t.id),
    inOutfit: (o: Outfit, t: SaveTarget) => t.type === 'item' && o.items.includes(t.id),

    setBoard(b: Board, t: SaveTarget, on: boolean) {
      const change = { [field(t)]: [t.id] };
      saveBoard.mutate({ id: b.id, owner: uid, ...(on ? { add: change } : { remove: change }) }, { onError: failed });
    },

    setOutfit(o: Outfit, itemId: string, on: boolean) {
      saveOutfit.mutate(
        { id: o.id, owner: uid, ...(on ? { addItem: itemId } : { removeItem: itemId }) },
        { onError: failed },
      );
    },

    /** Makes a board, optionally with something already on it; returns its id. */
    createBoard(name: string, t?: SaveTarget): string {
      const createId = newRecordId();
      saveBoard.mutate(
        { createId, owner: uid, name: name.trim(), add: t ? { [field(t)]: [t.id] } : undefined },
        { onError: failed },
      );
      return createId;
    },
  };
}
